/**
 * AdminPlayersPanel — admin-only list of every account in this world, with
 * a short game summary and a Remove action (server/admin/playerRoutes.js).
 *
 * Gated on isAdmin for visibility only (HUD button); the server re-checks
 * isAdmin on every /api/admin/* call. Admins can't remove themselves.
 */
import { EventBus } from '../../core/EventBus.js';
import { Modal } from '../components/Modal.js';
import { showConfirm } from '../components/dialogs.js';
import { fetchAdminPlayers, removeAdminPlayer } from '../../core/ServerConnection.js';
import { formatMoney } from '../../utils/formatting.js';

export class AdminPlayersPanel {
  constructor() {
    this.modal = new Modal('admin-players-modal', { title: 'Admin: Players' });
    this._players = [];
    this._filter = '';
    EventBus.on('ui:adminPlayersOpen', () => this.open());
  }

  async open() {
    this.modal.open();
    this.modal.setBody('<p class="panel-empty">Loading players…</p>');
    await this._load();
    this._render();
  }

  async _load() {
    const result = await fetchAdminPlayers();
    if (result && Array.isArray(result.players)) {
      this._players = result.players;
    } else {
      this._players = [];
      EventBus.emit('ui:notification', { message: result?.error || 'Failed to load players.', type: 'error' });
    }
  }

  _render() {
    const q = this._filter.trim().toLowerCase();
    const visible = q
      ? this._players.filter(p => p.username.toLowerCase().includes(q)
          || (p.game?.companyName || '').toLowerCase().includes(q))
      : this._players;
    const online = this._players.filter(p => p.online).length;

    this.modal.setBody(`
      <div class="admin-players-panel">
        <div class="admin-players-toolbar">
          <input type="search" id="admin-players-search" class="fb-input" placeholder="Search name or company" value="${this._esc(this._filter)}">
          <span class="admin-players-count">${this._players.length} players · ${online} online</span>
          <button class="btn btn-sm" id="admin-players-refresh">Refresh</button>
        </div>
        <div class="admin-players-list">
          ${visible.length ? visible.map(p => this._renderRow(p)).join('') : '<p class="panel-empty">No players found.</p>'}
        </div>
      </div>`);
    this._bindEvents();
  }

  _renderRow(p) {
    const self = p.isSelf;
    const g = p.game;
    const swatch = g?.companyColor ? `<span class="admin-player-swatch" style="background:${this._esc(g.companyColor)}"></span>` : '';
    const badges = [
      p.online ? '<span class="admin-player-badge admin-player-online">online</span>' : '',
      p.isAdmin ? '<span class="admin-player-badge">admin</span>' : '',
      ...p.login.map(l => `<span class="admin-player-badge">${l}</span>`)
    ].join('');
    const game = g
      ? `${swatch}${this._esc(g.companyName || '—')} · ${g.money != null ? formatMoney(g.money) : '—'} · ${g.ships} ship${g.ships === 1 ? '' : 's'}`
      : 'No game state';

    return `
      <div class="admin-player-row">
        <div class="admin-player-main">
          <div class="admin-player-name"><strong>${this._esc(p.username)}</strong>${self ? ' (you)' : ''} ${badges}</div>
          <div class="admin-player-game">${game}</div>
          <div class="admin-player-meta">Joined ${this._date(p.createdAt)} · Last login ${this._date(p.lastLogin)}</div>
        </div>
        ${self ? '' : `<button class="btn btn-sm btn-danger" data-remove-player="${this._esc(p.accountId)}">Remove</button>`}
      </div>`;
  }

  _bindEvents() {
    const search = document.getElementById('admin-players-search');
    search?.addEventListener('input', () => {
      this._filter = search.value;
      const pos = search.selectionStart;
      this._render();
      const next = document.getElementById('admin-players-search');
      next?.focus();
      next?.setSelectionRange(pos, pos);
    });
    document.getElementById('admin-players-refresh')?.addEventListener('click', () => this.open());
    this.modal.el.querySelectorAll('[data-remove-player]').forEach(btn => {
      btn.addEventListener('click', () => this._remove(btn.dataset.removePlayer));
    });
  }

  async _remove(accountId) {
    const p = this._players.find(x => x.accountId === accountId);
    if (!p) return;
    const ok = await showConfirm(
      `Permanently remove ${p.username}${p.game?.companyName ? ` (${p.game.companyName})` : ''}? `
        + 'Their account, ships and all progress are deleted and they are disconnected. This cannot be undone.',
      { title: 'Remove Player', confirmLabel: 'Remove' }
    );
    if (!ok) return;
    const result = await removeAdminPlayer(accountId);
    if (result?.success) {
      EventBus.emit('ui:notification', { message: `Removed ${result.username}.`, type: 'success' });
      this._players = this._players.filter(x => x.accountId !== accountId);
      this._render();
    } else {
      EventBus.emit('ui:notification', { message: result?.error || 'Failed to remove player.', type: 'error' });
    }
  }

  _date(ms) {
    return ms ? new Date(ms).toISOString().slice(0, 10) : '—';
  }

  _esc(str) {
    if (str == null) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
